import { UserGroupClient } from '@frontend/group/api';
import { SettingKey } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { syncTransactionUsers } from '../../utils/sync-wizard-transaction-data';
import { useScreen } from '../useScreen';

interface ViewProps {
    isLoading: boolean;
    senders: IUser[] | null;
    selectedSenders: (string | null)[];
    senderSubsets: (string[] | undefined)[];
    addSender: () => void;
    removeSender: (index: number) => void;
    updateSender: (index: number, senderId: string | null) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useSelectSenders = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [senders, setSenders] = useState<IUser[] | null>(null);
    const [selectedSenders, setSelectedSenders] = useState<(string | null)[]>(() => {
        const senderIds = ((store?.data?.senders as TransactionUser[]) || []).map((sender) => sender.user_id).filter((userId): userId is string => !!userId);
        return senderIds.length > 0 ? senderIds : [null];
    });
    const selectedSendersRef = useRef(selectedSenders);

    useEffect(() => {
        selectedSendersRef.current = selectedSenders;
    }, [selectedSenders]);

    useEffect(() => {
        const senderUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_SENDERS_USER_GROUP_ID)?.value;

        const loadSenders = async () => {
            try {
                if (senderUserGroupId && typeof senderUserGroupId === 'string') {
                    const response = await UserGroupClient.fetchUserGroupMembers({ group_id: senderUserGroupId });
                    const userResponse = await UserClient.resolveUsers(response.results.map((member) => member.entity_id));
                    setSenders(userResponse.results);
                } else {
                    setSenders(null);
                }

                if (store?.data.transaction?.id) {
                    const fetchedSenders = await TransactionUserClient.fetchTransactionUsers({
                        transaction_id: store.data.transaction.id,
                        type: TransactionUserType.SENDER
                    });

                    if (fetchedSenders.results.length > 0) {
                        updateWizardData({
                            senders: fetchedSenders.results
                        });
                        setSelectedSenders(fetchedSenders.results.map((sender) => sender.user_id));
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadSenders();
    }, []);

    const addSender = () => {
        setSelectedSenders([...selectedSendersRef.current, null]);
    };

    const removeSender = (index: number) => {
        setSelectedSenders(selectedSendersRef.current.filter((_, i) => i !== index));
    };

    const updateSender = useCallback((index: number, senderId: string | null) => {
        const updated = [...selectedSendersRef.current];
        updated[index] = senderId;
        setSelectedSenders(updated);
    }, []);

    const senderSubsets = useMemo(
        () =>
            selectedSenders.map((senderId) =>
                senders
                    ?.filter((sender) => {
                        const found = selectedSenders.find((id) => id === sender.id);
                        if (found) {
                            return senderId === found;
                        }
                        return true;
                    })
                    .map((sender) => sender.id)
            ),
        [senders, selectedSenders]
    );

    const canContinue = selectedSenders.some((sender) => sender !== null);

    const onSubmit = async () => {
        if (!store || !canContinue || !store.data.transaction?.id) return;

        setIsLoading(true);
        try {
            const accountId = store.wizard.account_id;
            const transactionId = store.data.transaction.id;
            const relevantSenders = selectedSendersRef.current.filter((sender): sender is string => sender !== null);
            const syncedSenders = await syncTransactionUsers(accountId, transactionId, TransactionUserType.SENDER, relevantSenders);

            updateWizardData({
                senders: syncedSenders
            });

            const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_SENDERS && step.trigger === ScreenTrigger.SUBMIT);
            navigateToNextStep(nextStep);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        senders,
        selectedSenders,
        senderSubsets,
        addSender,
        removeSender,
        updateSender,
        submitDisabled: !canContinue || !store?.data.transaction || isLoading,
        onSubmit,
        onBack
    };
};

export default useSelectSenders;
